import { memo, useState, useEffect, useCallback } from 'react'
import { sightingAPI } from '../../services/api'
import { useFavorites } from '../../hooks/useFavorites'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { useTranslation } from 'react-i18next'
import { CalendarDaysIcon, TagIcon, MapPinIcon, MapIcon, ShoppingBagIcon } from '@heroicons/react/24/outline'
import { createLogger } from '../../utils/logger'
import RecentSightingsSection from './activity/RecentSightingsSection'
import FavoritesSection from './activity/FavoritesSection'
import CollapsibleSection from './activity/CollapsibleSection'
import BookingsList from '../bookings/BookingsList'
import ProfileListingRequests from '../listings/ProfileListingRequests'
import MyListingsSection from '../listings/MyListingsSection'

const logger = createLogger('UserActivitySection')

/**
 * UserActivitySection - Collapsible user activity displays
 * 
 * Features:
 * - Recent sightings (paginated)
 * - Pending confirmations (paginated)
 * - Favorites list (paginated)
 * - Collapsible sections
 * 
 * Optimized by extracting section components
 * 
 * @idempotent - Pure component with controlled state
 * @atomic - Single responsibility: user activity display
 */
const UserActivitySection = memo(function UserActivitySection({
  user,
  userLocation,
  onTabChange
}) {
  const { t } = useTranslation()
  
  // Collapsible sections state
  const [showRecentSightings, setShowRecentSightings] = useState(false)
  const [showFavorites, setShowFavorites] = useState(false)
  const [showBookings, setShowBookings] = useState(false)
  const [showListingRequests, setShowListingRequests] = useState(false)
  const [showMyListings, setShowMyListings] = useState(false)

  // Only fetch listing requests once the section is opened — avoids 404 noise before backend is rebuilt
  const listingRequestsEnabled = showListingRequests

  // Eager pending count — fetched on mount so the badge shows before section is opened
  const [pendingRequestCount, setPendingRequestCount] = useState(0)
  useEffect(() => {
    import('../../services/api/listingAPI').then(({ listingAPI }) => {
      listingAPI.getIncomingRequests()
        .then((res) => {
          const pending = (res?.data?.requests || []).filter(r => r.status === 'pending').length
          setPendingRequestCount(pending)
        })
        .catch(() => {}) // silently skip — endpoint may not exist yet in older envs
    })
  }, [])

  // Pagination state
  const [sightingsPage, setSightingsPage] = useState(1)
  const [favoritesPage, setFavoritesPage] = useState(1)
  const itemsPerPage = 3

  // User's recent sightings
  const [userSightings, setUserSightings] = useState([])
  const { loading: loadingSightings, execute } = useAsyncOperation()

  const loadUserSightings = useCallback(async () => {
    if (!user?.id) {
      setUserSightings([])
      return
    }

    try {
      const response = await execute(
        () => sightingAPI.getUserSightings(user.id),
        { silent: true }
      )
      let sightingsData = response.data?.sightings || []
      
      if (!Array.isArray(sightingsData)) {
        logger.error('Expected sightings array but got non-array data', null, { 
          dataType: typeof sightingsData,
          userId: user?.id 
        })
        sightingsData = []
      }
      
      setUserSightings(sightingsData.slice(0, 5))
    } catch (error) {
      logger.error('Failed to load user sightings', error, { userId: user?.id })
      setUserSightings([])
    }
  }, [user?.id, execute])

  useEffect(() => {
    loadUserSightings()
  }, [loadUserSightings])

  // Favorites hook
  const { 
    favorites, 
    loading: loadingFavorites, 
    removeFavorite,
    updateFavoriteNotify,
    refetchFavorites 
  } = useFavorites(user, userLocation)

  const handleRemoveFavorite = useCallback(async (entityId) => {
    await removeFavorite(entityId)
    refetchFavorites()
  }, [removeFavorite, refetchFavorites])

  const handleNotifyToggle = useCallback((entityId, notifyOnSpot) => {
    updateFavoriteNotify(entityId, notifyOnSpot)
  }, [updateFavoriteNotify])

  return (
    <div className="space-y-0">
      {/* ── Community activity ─────────────────────────────────── */}
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide px-1 pt-1 pb-0.5">
        {t('profile.sections.community', 'Community')}
      </p>

      <RecentSightingsSection
        isExpanded={showRecentSightings}
        onToggle={() => setShowRecentSightings(!showRecentSightings)}
        sightings={userSightings}
        loading={loadingSightings}
        currentPage={sightingsPage}
        onPageChange={setSightingsPage}
        itemsPerPage={itemsPerPage}
        userLocation={userLocation}
        t={t}
      />

      <FavoritesSection
        isExpanded={showFavorites}
        onToggle={() => setShowFavorites(!showFavorites)}
        favorites={favorites}
        loading={loadingFavorites}
        currentPage={favoritesPage}
        onPageChange={setFavoritesPage}
        itemsPerPage={itemsPerPage}
        onRemove={handleRemoveFavorite}
        onNotifyToggle={handleNotifyToggle}
        t={t}
      />

      {/* ── Market / transactions ────────────────────────────────── */}
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide px-1 pt-3 pb-0.5">
        {t('profile.sections.market', 'Market')}
      </p>

      <CollapsibleSection
        title={t('listings.myListings', 'My Listings')}
        icon={ShoppingBagIcon}
        isExpanded={showMyListings}
        onToggle={() => setShowMyListings(!showMyListings)}
        iconColor="text-amber-500"
        badgeColor="bg-amber-50 text-amber-700"
      >
        {showMyListings && <MyListingsSection />}
      </CollapsibleSection>

      <CollapsibleSection
        title={t('listings.request.inboxTitle', 'Listing Requests')}
        icon={TagIcon}
        isExpanded={showListingRequests}
        onToggle={() => setShowListingRequests(!showListingRequests)}
        iconColor="text-accent"
        badgeColor="bg-accent/10 text-accent"
        count={pendingRequestCount}
      >
        <ProfileListingRequests onTabChange={onTabChange} enabled={listingRequestsEnabled} />
      </CollapsibleSection>

      {/* ── Services ─────────────────────────────────────────────── */}
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide px-1 pt-3 pb-0.5">
        {t('profile.sections.services', 'Services')}
      </p>

      <CollapsibleSection
        title={t('bookings.myBookings', 'My Bookings')}
        icon={CalendarDaysIcon}
        isExpanded={showBookings}
        onToggle={() => setShowBookings(!showBookings)}
        iconColor="text-primary"
        badgeColor="bg-primary/10 text-primary"
      >
        <BookingsList />
      </CollapsibleSection>
    </div>
  )
})

export default UserActivitySection
